import { property, remote, supertypeClass } from '@haventech/amorphic';
import { Isomorphic } from '@hit/amorphic-types';
import { Agent } from '../customer/Agent';
import { AgentProxy } from '../agent/AgentProxy';
import { OpsProxy } from '../agent/OpsProxy';
import { WholesaleProxy } from '../agent/WholesaleProxy';
import { TeleInterviewer } from './TeleInterviewer';
import { Entitlement } from '../types/CommonTypes';

export { Entitlement } from '../types/CommonTypes';

@supertypeClass({ toClient: true, toServer: true })
export class UserPrincipal extends Isomorphic {
  // Entitlement is set dynamically in the session when a principal logs in. This is a bit
  // different from standard architecture where the principal and entitlement are pulled from
  // a database. This is because the agent application can create the principal on a
  // first login
  @property({ toServer: false })
  entitlement: Entitlement;

  @property({ toServer: false })
  private agent: Agent;

  @property({ toServer: false })
  private proxy: AgentProxy;

  @property({ toServer: false })
  private opsProxy: OpsProxy;

  @property({ toServer: false })
  private wholesaleProxy: WholesaleProxy;

  @property({ toServer: false })
  private teleInterviewer: TeleInterviewer;

  static new(entitlement: Entitlement, user: Agent | AgentProxy | OpsProxy | WholesaleProxy | TeleInterviewer) {
    return new UserPrincipal().init(entitlement, user);
  }

  init(entitlement: Entitlement, user: Agent | AgentProxy | OpsProxy | WholesaleProxy | TeleInterviewer) {
    this.entitlement = entitlement;

    switch (this.entitlement) {
      case Entitlement.AGENT:
      case Entitlement.DUAL_ROLE:
        this.agent = user as Agent;
        break;
      case Entitlement.AGENT_PROXY:
        this.proxy = user as AgentProxy;
        break;
      case Entitlement.OPS_PROXY:
        this.opsProxy = user as OpsProxy;
        break;
      case Entitlement.WHOLESALE_PROXY:
        this.wholesaleProxy = user as WholesaleProxy;
        break;
      case Entitlement.TELE_INTERVIEWER:
        this.teleInterviewer = user as TeleInterviewer;
        break;
    }
    return this;
  }

  public setAgent(agent: Agent) {
    this.agent = agent;
  }

  public setProxy(proxy: AgentProxy) {
    this.proxy = proxy;
  }

  // get the currently logged in user, can be an agent (including regular agent, Brokerage director, or Sales Manager)
  // proxy or opsProxy or wholesaleProxy or teleInterviewer
  public get user() {
    return this.agent || this.proxy || this.opsProxy || this.wholesaleProxy || this.teleInterviewer;
  }

  public hasProxyLoggedIn(): boolean {
    return UserPrincipal.hasProxyLoggedIn(this.entitlement);
  }

  public hasWholesaleProxyLoggedIn(): boolean {
    return this.entitlement === Entitlement.WHOLESALE_PROXY;
  }
  // This static function is needed because UserPrincipal is only available on the controllers.
  // Data model classes, such as Application.ts, need to use this static function.
  public static hasProxyLoggedIn(entitlement: Entitlement): boolean {
    return (
      entitlement === Entitlement.AGENT_PROXY ||
      entitlement === Entitlement.OPS_PROXY ||
      entitlement === Entitlement.WHOLESALE_PROXY
    );
  }
}
